import { useState } from 'react';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Title from '../../shared/Title';
import FFPaper from '../../shared/FFPaper';
import FFLinearProgress from '../../shared/FFLinearProgress';
import DropDownButton, { DropDownMenu } from '../../shared/DropDownButton';
import TimeOfDayPieChart, { TimeOfDayData } from './TimeOfDayPieChart';
import { useTranslation } from 'react-i18next';

const exportMenus: DropDownMenu[] = [{ label: 'PDF', value: 'pdf' }, { label: 'CSV', value: 'csv' }];
const menus: DropDownMenu[] = [
    { label: 'By Week', value: 'week' },
    { label: 'By Month', value: 'month' },
    { label: 'By Year', value: 'year' }
];
const timeData: TimeOfDayData[] = [
    {
        name: 'Early Morning',
        time: '5am-8am',
        count: 3537
    },
    {
        name: 'Morning',
        time: '8am-11am',
        count: 3383
    },
    {
        name: 'Lunch',
        time: '11am-12pm',
        count: 2537
    },
    {
        name: 'Afternoon',
        time: '12pm-5pm',
        count: 1237
    },
    {
        name: 'Evening',
        time: '5pm-12am',
        count: 220
    }
];


function roundup(v: number): number {
    return Math.pow(10, Math.ceil(Math.log10(v)));
}

let maxValue: number | undefined;

function getMaxValue(data: { count: number }[]): number {
    const max = data.reduce((prev: { count: number }, current: { count: number }) => {
        return (prev.count > current.count) ? prev : current
    });
    const maxVal = max.count;
    const maxRoundUp = roundup(maxVal);
    const nearerMaxValRoundUp = Math.ceil((maxVal / maxRoundUp) * 10);
    const maxRoundedValue = nearerMaxValRoundUp * (maxRoundUp / 10);
    return maxRoundedValue;
}

function getPercentage(data: { count: number }[], n: number): number {
    if (maxValue === undefined) {
        maxValue = getMaxValue(data);
    }
    const percentage = (n / maxValue) * 100;
    return percentage;
}


function TimeOfDayBarChart({ data }: { data: TimeOfDayData[] }) {

    return <Stack direction="column"
        spacing={2}
        sx={{
            pb: 1,
            pr: 4,
            pt: 2,
            pl: {
                xs: 0,
                sm: 1,
                md: 1
            }
        }}>
        {
            data.map((d, idx) => {

                return <Stack key={idx} direction="row" spacing={2}>
                    <Stack justifyContent="space-between" spacing={1}>
                        <Typography color="primary"  
                        sx={{ 
                            fontWeight: 'bold',
                            fontSize: {
                                xs: 12,
                                md: 13,
                                lg: 14
                            }
                        }}>{d.name}</Typography>
                        <Typography fontSize={14} pr={1}>{d.time}</Typography>
                    </Stack>
                    <FFLinearProgress big={true} progressValue={getPercentage(timeData, d.count)} />
                    <Typography 
                    sx={{
                        fontSize: {
                            xs: 12,
                            md: 13,
                            lg: 14
                        },
                        pr: 1
                    }}
                    >{d.count.toLocaleString()}</Typography>
                </Stack>

            })
        }

        <Divider sx={{
            borderColor: 'text.secondary'
        }}
        />

        <Stack
            spacing={4}
            direction="row"
            sx={{
                justifyContent: 'flex-end',
                alignItems: 'center'
            }}>
            <Typography>
                Total Count
            </Typography>
            <Typography color="secondary" sx={{
                fontSize: 18,
                fontWeight: 'bold'
            }}>
                {timeData.reduce((a: number, b: { count: number }) => a + b.count, 0).toLocaleString()}
            </Typography>
        </Stack>
    </Stack>
}

export default function TimeOfDayCard() {
    const [activeChart, setActiveChart] = useState('table');
    const { t } = useTranslation();

    const toggleChart = () => {
        if (activeChart === 'table') {
            setActiveChart('donut');
        } else {
            setActiveChart('table')
        }
    }

    return (
        <Grid item={true} xs={12} md={12} lg={9} >
            <FFPaper
                sx={{
                    alignItems: 'stretch',
                    position: 'relative',
                    flexDirection: 'row',
                    height: '100%',
                }}
            >

                <Grid item={true} xs={12} 
                
                sx={{ display: 'flex', flexDirection: 'column', position: 'relative', 
                
                    pb: 1, pt: 1, 
                    pl: {
                        xs: 0,
                        sm: 1,
                        md: 1
                    }, 
                    pr: {
                        xs: 0,
                        sm: 1,
                        md: 1
                    }, 
                    }}>
                    <Stack direction="row"
                    sx={{
                        flexWrap: 'wrap'
                    }}
                    
                    spacing={2}>
                        <Title big={true} info="Measures amount of traffic during a specific time of day">Time Of Day</Title>
                        <Stack direction="row" spacing={2} 
                            sx={{ 
                                pt: {
                                    xs: 1,
                                    md: 0
                                },
                                pr: 1, 
                                pb: 1, 
                                mb: 1, 
                                flexWrap: 'wrap' }}>
                            <DropDownButton defaultLabel='By Week' fontSize={14} data={menus} />
                            <Button
                                onClick={toggleChart}
                                variant="contained"
                                disableElevation={true}
                                sx={{
                                    backgroundColor: 'action.active',
                                    color: 'text.primary'
                                }} >
                                {activeChart === 'table' && 'Donut View'}
                                {activeChart === 'donut' && 'Table View'}
                            </Button>
                            <DropDownButton boxShadow={false}
                                defaultLabel={t('app_export_data')}
                                color="text.primary" 
                                backgroundColor="button.main" 
                                fontSize={14} 
                                data={exportMenus} />
                        </Stack>
                    </Stack>

                    {activeChart === 'table' && <TimeOfDayBarChart data={timeData} />}
                    {activeChart === 'donut' && <TimeOfDayPieChart data={timeData} />}
                </Grid>

            </FFPaper>
        </Grid>
    )
}